import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  CompareReportDataPoint,
  QuantifyReportDataPoint,
  QuantifyState,
} from '@modules/quantify/store/types';
import {
  getDefaultSelectedDataPoints,
  getUpdatedSortyByIfRequired,
} from '@modules/quantify/utils';
import { useCallback, useRef } from 'react';
import { shallow } from 'zustand/shallow';

interface useUpdateQuantifyStoreIfRequiredParams {
  isInitialFetch?: boolean;
}

export function useUpdateQuantifyStoreIfRequired(
  options: useUpdateQuantifyStoreIfRequiredParams,
) {
  const { isInitialFetch = false } = options;
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const state = useQuantifyStore(({ metadata, reportData, visual, query }) => {
    return {
      isCompareToPresent: Boolean(query.compareTo),
      visibleDataPoints: metadata.visibleDataPoints,
      sortBy: metadata.sortBy,
      shouldShowTopDataPoints: metadata.shouldShowTopDataPoints,
      topDataPointsSelectionCount: metadata.topDataPointsSelectionCount,
      quantifyTableRows: reportData?.quantify.tableRows,
      compareTableRows: reportData?.compare.tableRows,
      pageNumber: visual.pageNumber,
      activeVisualisationType: metadata.activeVisualisationType,
      shouldShowAnomalies: metadata.shouldShowAnomalies,
      searchTerm: visual.searchTerm,
      shouldShowAllValues: metadata.shouldShowAllValues,
    };
  }, shallow);
  const dependencies = {
    ...state,
    isInitialFetch,
  };

  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const updateQuantifyStoreIfRequired = useCallback(
    ({
      quantifyTableRows: newQuantifyTableRows = [],
      compareTableRows: newCompareTableRows = [],
      aggregateHits,
    }: {
      quantifyTableRows?: QuantifyReportDataPoint[];
      compareTableRows?: CompareReportDataPoint[];
      aggregateHits: number;
    }) => {
      const isTableSpecificDataQuery =
        !!dependenciesRef.current.searchTerm ||
        !dependenciesRef.current.shouldShowAllValues;

      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: isTableSpecificDataQuery
          ? { tableAggregateHits: aggregateHits }
          : {
              totalAggregateHits: aggregateHits,
              tableAggregateHits: aggregateHits,
            },
      });

      let compareTableRows;
      let quantifyTableRows;
      const shouldInitTableRows =
        dependenciesRef.current.isInitialFetch ||
        dependenciesRef.current.pageNumber === 1;

      const isCompareToPresent = dependenciesRef.current.isCompareToPresent;

      if (isCompareToPresent) {
        compareTableRows = shouldInitTableRows
          ? newCompareTableRows
          : [
              ...(dependenciesRef.current.compareTableRows || []),
              ...newCompareTableRows,
            ];
        dispatch({
          type: QuantifyActionType.SET_REPORT_DATA,
          payload: {
            compare: {
              tableRows: compareTableRows,
            },
          },
        });
      } else {
        quantifyTableRows = shouldInitTableRows
          ? newQuantifyTableRows
          : [
              ...(dependenciesRef.current.quantifyTableRows || []),
              ...newQuantifyTableRows,
            ];
        dispatch({
          type: QuantifyActionType.SET_REPORT_DATA,
          payload: {
            quantify: {
              tableRows: quantifyTableRows,
            },
          },
        });
      }

      const updatedSortBy = isCompareToPresent
        ? dependenciesRef.current.sortBy
        : getUpdatedSortyByIfRequired({
            tableRows: quantifyTableRows ?? [],
            sortBy: dependenciesRef.current.sortBy,
            shouldShowAnomalies: dependenciesRef.current.shouldShowAnomalies,
          });

      const payload: Partial<QuantifyState['metadata']> = {
        sortBy: updatedSortBy,
      };

      if (
        // Don't reset visibleDataPoints on presence of searchTerm
        !dependenciesRef.current.searchTerm &&
        dependenciesRef.current.shouldShowTopDataPoints
      ) {
        payload.visibleDataPoints = getDefaultSelectedDataPoints({
          reportDataPoints:
            (isCompareToPresent ? compareTableRows : quantifyTableRows) ?? [],
          visualisationType: dependenciesRef.current.activeVisualisationType,
          selectionCount: dependenciesRef.current.topDataPointsSelectionCount,
        });
      }

      dispatch({
        type: dependenciesRef.current.isInitialFetch
          ? QuantifyActionType.INIT_METADATA
          : QuantifyActionType.SET_METADATA,
        payload,
      });
    },
    [dispatch],
  );

  return updateQuantifyStoreIfRequired;
}
